import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DoneAll as AllIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * ModulePermissionsTable Component
 * Displays all modules with their permissions in a simple table format
 * Modules on the left, permissions (View, Edit, Create, Delete) as column headers
 */
const ModulePermissionsTable = ({
  modules = [],
  permissions = {},
  onChange,
  loading = false,
  disabled = false,
  mode = 'role',
  overrides = {},
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter modules based on search
  const filteredModules = modules.filter(
    (module) =>
      module.ModuleName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      module.Description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Organize modules into parent-child structure
  const organizedModules = React.useMemo(() => {
    const result = [];
    const parentModules = filteredModules.filter(m => !m.ParentModuleId && !m.parentModuleId);
    
    parentModules.forEach(parent => {
      result.push({ ...parent, isParent: true, level: 0 });
      
      // Add children from ChildModules array if it exists
      if (parent.ChildModules && Array.isArray(parent.ChildModules) && parent.ChildModules.length > 0) {
        parent.ChildModules.forEach(child => {
          result.push({ ...child, isChild: true, level: 1, parentName: parent.ModuleName });
        });
      }
    });
    
    return result;
  }, [filteredModules]);

  const handlePermissionChange = (moduleName, permissionType, value) => {
    const modulePerms = permissions[moduleName] || {
      CanView: false,
      CanEdit: false,
      CanCreate: false,
      CanDelete: false,
    };

    onChange({
      ...permissions,
      [moduleName]: {
        ...modulePerms,
        [permissionType]: value,
      },
    });
  };

  // Toggle all permissions for a module at once
  const handleSelectAll = (moduleName, value) => {
    onChange({
      ...permissions,
      [moduleName]: {
        CanView: value,
        CanEdit: value,
        CanCreate: value,
        CanDelete: value,
      },
    });
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('access_management.loading_modules')}...
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder={t('access_management.search_modules')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* User Override Notice */}
      {mode === 'user' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('access_management.user_override_notice')}
        </Alert>
      )}

      {/* No Results */}
      {organizedModules.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {searchQuery 
              ? t('access_management.no_modules_found')
              : t('access_management.no_modules_available')
            }
          </Typography>
        </Paper>
      )}

      {/* Permissions Table */}
      {organizedModules.length > 0 && (
        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#667eea' }}>
                <TableCell 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '1rem',
                    minWidth: 200,
                  }}
                >
                  {t('access_management.module')}
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <AllIcon fontSize="small" />
                    {t('access_management.all') || 'All'}
                  </Box>
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <ViewIcon fontSize="small" />
                    {t('access_management.view')}
                  </Box>
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <EditIcon fontSize="small" />
                    {t('access_management.edit')}
                  </Box>
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <AddIcon fontSize="small" />
                    {t('access_management.create')}
                  </Box>
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <DeleteIcon fontSize="small" />
                    {t('access_management.delete')}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizedModules.map((module, index) => {
                const moduleName = module.ModuleName;
                const modulePerms = permissions[moduleName] || {
                  CanView: false,
                  CanEdit: false,
                  CanCreate: false,
                  CanDelete: false,
                };

                return (
                  <TableRow
                    key={module.Id || module.id || `${moduleName}-${index}`}
                    sx={{
                      '&:nth-of-type(odd)': {
                        bgcolor: module.isChild ? '#fff8f0' : '#f9f9f9',
                      },
                      '&:hover': {
                        bgcolor: module.isChild ? '#fff3e0' : '#f0f0f0',
                      },
                      bgcolor: module.isChild ? '#fff8f0' : 'transparent',
                      borderLeft: module.isChild ? '3px solid #ff9800' : 'none',
                    }}
                  >
                    <TableCell sx={{ pl: module.isChild ? 4 : 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {module.isChild && (
                          <Typography
                            variant="caption"
                            sx={{
                              px: 0.75,
                              py: 0.25,
                              bgcolor: '#ff9800',
                              color: 'white',
                              borderRadius: 0.5,
                              fontWeight: 600,
                              fontSize: '0.65rem',
                            }}
                          >
                            SUB
                          </Typography>
                        )}
                        <Box>
                          <Typography variant="body1" fontWeight={module.isParent ? 700 : 600}>
                            {module.ModuleName}
                          </Typography>
                          {module.Description && (
                            <Typography variant="caption" color="text.secondary">
                              {module.Description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={
                          modulePerms.CanView &&
                          modulePerms.CanEdit &&
                          modulePerms.CanCreate &&
                          modulePerms.CanDelete
                        }
                        indeterminate={
                          (modulePerms.CanView ||
                            modulePerms.CanEdit ||
                            modulePerms.CanCreate ||
                            modulePerms.CanDelete) &&
                          !(
                            modulePerms.CanView &&
                            modulePerms.CanEdit &&
                            modulePerms.CanCreate &&
                            modulePerms.CanDelete
                          )
                        }
                        onChange={(e) =>
                          handleSelectAll(moduleName, e.target.checked)
                        }
                        disabled={disabled}
                        sx={{
                          color: '#667eea',
                          '&.Mui-checked': {
                            color: '#667eea',
                          },
                          '&.MuiCheckbox-indeterminate': {
                            color: '#667eea',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={modulePerms.CanView || false}
                        onChange={(e) =>
                          handlePermissionChange(
                            moduleName,
                            'CanView',
                            e.target.checked
                          )
                        }
                        disabled={disabled}
                        sx={{
                          color: '#2196f3',
                          '&.Mui-checked': {
                            color: '#2196f3',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={modulePerms.CanEdit || false}
                        onChange={(e) =>
                          handlePermissionChange(
                            moduleName,
                            'CanEdit',
                            e.target.checked
                          )
                        }
                        disabled={disabled}
                        sx={{
                          color: '#ff9800',
                          '&.Mui-checked': {
                            color: '#ff9800',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={modulePerms.CanCreate || false}
                        onChange={(e) =>
                          handlePermissionChange(
                            moduleName,
                            'CanCreate',
                            e.target.checked
                          )
                        }
                        disabled={disabled}
                        sx={{
                          color: '#4caf50',
                          '&.Mui-checked': {
                            color: '#4caf50',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={modulePerms.CanDelete || false}
                        onChange={(e) =>
                          handlePermissionChange(
                            moduleName,
                            'CanDelete',
                            e.target.checked
                          )
                        }
                        disabled={disabled}
                        sx={{
                          color: '#f44336',
                          '&.Mui-checked': {
                            color: '#f44336',
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ModulePermissionsTable;
